import { BillingRecordPicker } from "../billing/BillingRecordPicker";
import { useAuth } from "../../contexts/AuthContext";
import { WorkOrderReadiness } from "../jobs/JobWorkspace";
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Stack,
  Grid,
  Alert,
  FormHelperText,
  Typography,
} from "@mui/joy";
import { FloppyDisk as Save, XCircle as Cancel } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { workOrdersApi } from "../../lib/apiSubcontractors";
import { apiClient } from "../../lib/api";
import type { WorkOrderFormData } from "../../types/subcontractors";
import {
  applyPrefill,
  formatAddressLine,
  recordAddressFields,
} from "../../utils/prefill";

interface WorkOrderFormProps {
  jobId?: number;
  subcontractorId?: number;
  initialValues?: Partial<WorkOrderFormData>;
  workOrder?: any;
  onSuccess?: (record?: any) => void;
  onCancel?: () => void;
}

export const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  jobId,
  subcontractorId,
  initialValues,
  workOrder,
  onSuccess,
  onCancel,
}) => {
  const isEditMode = !!workOrder;

  const { user } = useAuth();
  const templateKey = `work-order-templates:${user?.organization}:${user?.id}`;
  const [templates, setTemplates] = useState<
    Record<string, Partial<WorkOrderFormData>>
  >(() => {
    try {
      return JSON.parse(localStorage.getItem(templateKey) || "{}");
    } catch {
      return {};
    }
  });
  const [templateName, setTemplateName] = useState("");
  const builtin: Record<string, Partial<WorkOrderFormData>> = {
    "Roof replacement": {
      scope_of_work:
        "Confirm measured scope and materials. Protect property, remove specified roofing, inspect decking, install approved roofing system and clean the site.",
      special_instructions:
        "Confirm access, material staging and disposal arrangements.",
      checklist: [
        { label: "Property protected", done: false },
        { label: "Roofing and flashing inspected", done: false },
        { label: "Site cleaned and photos captured", done: false },
      ],
    },
    "Gutter installation": {
      scope_of_work:
        "Confirm gutter lengths, outlets and downspout routing. Install the specified system and test drainage.",
      checklist: [
        { label: "Routing approved", done: false },
        { label: "Drainage tested", done: false },
      ],
    },
    "Roof repair": {
      scope_of_work:
        "Confirm the defect and repair area. Complete the approved repair and document the finished work.",
      checklist: [
        { label: "Repair area documented", done: false },
        { label: "Repair inspected", done: false },
      ],
    },
  };
  const [formData, setFormData] = useState<WorkOrderFormData>({
    job: workOrder?.job || jobId || 0,
    subcontractor: workOrder?.subcontractor || subcontractorId || 0,
    title: workOrder?.title || "",
    description: workOrder?.description || "",
    scope_of_work: workOrder?.scope_of_work || "",
    start_date: workOrder?.start_date || "",
    end_date: workOrder?.end_date || "",
    estimated_cost: workOrder?.estimated_cost || undefined,
    agreed_cost: workOrder?.agreed_cost || 0,
    special_instructions: workOrder?.special_instructions || "",
    checklist: workOrder?.checklist || [],
    ...initialValues,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form data when workOrder prop changes (for edit mode)
  useEffect(() => {
    if (workOrder) {
      setFormData({
        job: workOrder.job || jobId || 0,
        subcontractor: workOrder.subcontractor || 0,
        title: workOrder.title || "",
        description: workOrder.description || "",
        scope_of_work: workOrder.scope_of_work || "",
        start_date: workOrder.start_date || "",
        end_date: workOrder.end_date || "",
        estimated_cost: workOrder.estimated_cost || undefined,
        agreed_cost: workOrder.agreed_cost || 0,
        special_instructions: workOrder.special_instructions || "",
        checklist: workOrder.checklist || [],
      });
    }
  }, [workOrder, jobId]);

  const { data: selectedJob } = useQuery({
    queryKey: ["work-order-job", formData.job],
    enabled: !!formData.job,
    queryFn: async () =>
      (await apiClient.get<any>(`/api/jobs/${formData.job}/`)).data,
  });
  const jobSiteAddress = formatAddressLine(recordAddressFields(selectedJob));
  const { data: acceptedScopes = [] } = useQuery({
    queryKey: ["job-proposal-services", formData.job], enabled: !!formData.job && !isEditMode,
    queryFn: async () => (await apiClient.get<{ key: string; name: string; proposal_number: string; scope_of_work: string; special_instructions: string }[]>(`/api/jobs/${formData.job}/proposal-services/`)).data,
  });


  // Default a new work order from the job it hangs off. The build date, the
  // site and the job's own description are already on record, so retyping
  // them here is pure re-entry. Edit mode is left alone, and only fields the
  // user has not filled in are seeded.
  //
  // The ref holds what this effect last wrote so that re-pointing the form at
  // a different job replaces the old job's guesses — a work order titled with
  // job A's number but filed against job B is worse than an empty title. A
  // value the user typed never matches the ref and is left alone.
  const seededFromJobRef = useRef<Partial<WorkOrderFormData>>({});
  useEffect(() => {
    if (isEditMode || !selectedJob) return;

    const seeded: Partial<WorkOrderFormData> = {
      // A job with no street address on file would otherwise seed a title
      // ending in a dangling separator.
      title: [selectedJob.job_number, selectedJob.address_1]
        .filter(Boolean)
        .join(" - "),
      description: selectedJob.description,
      start_date: selectedJob.build_date,
    };
    const previous = seededFromJobRef.current;
    seededFromJobRef.current = seeded;

    setFormData((prev) =>
      applyPrefill(
        {
          ...prev,
          title: prev.title === previous.title ? "" : prev.title,
          description:
            prev.description === previous.description ? "" : prev.description,
          start_date:
            prev.start_date === previous.start_date ? "" : prev.start_date,
        },
        seeded,
      ),
    );
  }, [selectedJob, isEditMode]);

  const saveMutation = useMutation({
    mutationFn: async (data: WorkOrderFormData) => {
      if (isEditMode) {
        return workOrdersApi.update(workOrder.id, data);
      }
      return workOrdersApi.create(data);
    },
    onSuccess: (record) => {
      if (onSuccess) onSuccess(record);
    },
    onError: (error: any) => {
      if (error.responseData) {
        setErrors(error.responseData);
      } else if (error.response?.data) {
        setErrors(error.response.data);
      }
    },
  });

  const handleChange = (field: keyof WorkOrderFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.job) {
      newErrors.job = "Job is required";
    }
    if (!formData.subcontractor) {
      newErrors.subcontractor = "Subcontractor is required";
    }
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.scope_of_work.trim()) {
      newErrors.scope_of_work = "Scope of work is required";
    }
    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }
    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }
    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_date > formData.end_date
    ) {
      newErrors.end_date = "End date must be after start date";
    }
    if (!formData.agreed_cost || formData.agreed_cost <= 0) {
      newErrors.agreed_cost =
        "Agreed cost is required and must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      saveMutation.mutate(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <WorkOrderReadiness order={formData} />
      <Stack spacing={3}>
        {acceptedScopes.length > 0 && <FormControl><FormLabel>Use an accepted service scope</FormLabel><Select placeholder="Choose accepted work" onChange={(_, key) => {
          const scope = acceptedScopes.find(item => item.key === key);
          if (scope) setFormData(current => ({ ...current, title: scope.name, scope_of_work: scope.scope_of_work, special_instructions: scope.special_instructions }));
        }}>{acceptedScopes.map(scope => <Option key={scope.key} value={scope.key}>{scope.name} · {scope.proposal_number}</Option>)}</Select><FormHelperText>Copies accepted specifications. Confirm crew, schedule and agreed subcontractor cost before saving.</FormHelperText></FormControl>}
        <FormControl>
          <FormLabel>Completion checklist (one item per line)</FormLabel>
          <Textarea
            minRows={2}
            value={formData.checklist?.map((i) => i.label).join("\n") || ""}
            onChange={(e) =>
              handleChange(
                "checklist",
                e.target.value
                  .split("\n")
                  .filter((v) => v.trim())
                  .map((label) => ({ label, done: false })),
              )
            }
          />
        </FormControl>
        <Grid container spacing={2}>
          {/* The Job select is hidden when the job is preset, which would
              otherwise hide the site address the subcontractor is being sent to. */}
          {jobId && selectedJob && (
            <Grid xs={12}>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Job {selectedJob.job_number}
                {jobSiteAddress ? ` · ${jobSiteAddress}` : ""}
              </Typography>
            </Grid>
          )}

          {!jobId && (
            <Grid xs={12}>
              <BillingRecordPicker
                label="Job"
                endpoint="/api/jobs/"
                value={formData.job}
                onChange={(row) => handleChange("job", row?.id || 0)}
              />
              {errors.job && <Alert color="danger">{errors.job}</Alert>}
            </Grid>
          )}
          <Grid xs={12}>
            <BillingRecordPicker
              label="Subcontractor"
              endpoint="/api/subcontractors/?status=active"
              value={formData.subcontractor}
              onChange={(row) => handleChange("subcontractor", row?.id || 0)}
            />
            {errors.subcontractor && (
              <Alert color="danger">{errors.subcontractor}</Alert>
            )}
          </Grid>
          {!isEditMode && (
            <Grid xs={12}>
              <Stack spacing={1}>
                <Typography level="title-md">Reuse a scope</Typography>
                <Select
                  placeholder="Choose a scope template"
                  onChange={(_, v) => {
                    const selected = { ...builtin, ...templates }[String(v)];
                    if (selected)
                      setFormData((prev) => ({ ...prev, ...selected }));
                  }}
                >
                  {Object.keys({ ...builtin, ...templates }).map((name) => (
                    <Option key={name} value={name}>
                      {name}
                    </Option>
                  ))}
                </Select>
                <Input
                  aria-label="Template name"
                  placeholder="Name a custom scope template"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
                <Button
                  variant="outlined"
                  disabled={
                    !templateName.trim() || !formData.scope_of_work.trim()
                  }
                  onClick={() => {
                    const next = {
                      ...templates,
                      [templateName.trim()]: {
                        scope_of_work: formData.scope_of_work,
                        special_instructions: formData.special_instructions,
                        checklist: formData.checklist,
                      },
                    };
                    localStorage.setItem(templateKey, JSON.stringify(next));
                    setTemplates(next);
                    setTemplateName("");
                  }}
                >
                  Save scope template
                </Button>
                <Typography level="body-sm">
                  Templates are saved for your account in this browser. Review
                  dates, assignment and price for every new order.
                </Typography>
              </Stack>
            </Grid>
          )}
          <Grid xs={12}>
            <FormControl error={!!errors.title}>
              <FormLabel required>Work Order Title</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g., Roof Installation - Main Building"
              />
              {errors.title && <FormHelperText>{errors.title}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid xs={12}>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={2}
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description of the work"
              />
            </FormControl>
          </Grid>

          <Grid xs={12}>
            <FormControl error={!!errors.scope_of_work}>
              <FormLabel required>Scope of Work</FormLabel>
              <Textarea
                minRows={4}
                value={formData.scope_of_work}
                onChange={(e) => handleChange("scope_of_work", e.target.value)}
                placeholder="Detailed scope of work to be performed..."
              />
              {errors.scope_of_work && (
                <FormHelperText>{errors.scope_of_work}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid xs={12} md={6}>
            <FormControl error={!!errors.start_date}>
              <FormLabel required>Start Date</FormLabel>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
              />
              {errors.start_date && (
                <FormHelperText>{errors.start_date}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid xs={12} md={6}>
            <FormControl error={!!errors.end_date}>
              <FormLabel required>End Date</FormLabel>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
              />
              {errors.end_date && (
                <FormHelperText>{errors.end_date}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid xs={12} md={6}>
            <FormControl>
              <FormLabel>Estimated Cost</FormLabel>
              <Input
                type="number"
                value={formData.estimated_cost || ""}
                onChange={(e) =>
                  handleChange(
                    "estimated_cost",
                    e.target.value ? parseFloat(e.target.value) : undefined,
                  )
                }
                startDecorator="$"
                placeholder="0.00"
              />
            </FormControl>
          </Grid>

          <Grid xs={12} md={6}>
            <FormControl error={!!errors.agreed_cost}>
              <FormLabel required>Agreed Cost</FormLabel>
              <Input
                type="number"
                value={formData.agreed_cost}
                onChange={(e) =>
                  handleChange("agreed_cost", parseFloat(e.target.value) || 0)
                }
                startDecorator="$"
                placeholder="0.00"
              />
              {errors.agreed_cost && (
                <FormHelperText>{errors.agreed_cost}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid xs={12}>
            <FormControl>
              <FormLabel>Special Instructions</FormLabel>
              <Textarea
                minRows={3}
                value={formData.special_instructions}
                onChange={(e) =>
                  handleChange("special_instructions", e.target.value)
                }
                placeholder="Any special instructions, safety requirements, access notes, etc."
              />
            </FormControl>
          </Grid>
        </Grid>

        {/* Form Actions */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {onCancel && (
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<Cancel />}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            startDecorator={<Save />}
            loading={saveMutation.isPending}
          >
            {isEditMode ? "Update" : "Create"} Work Order
          </Button>
        </Box>

        {saveMutation.isError && (
          <Alert color="danger">
            Error saving work order. Please check the form and try again.
          </Alert>
        )}
      </Stack>
    </form>
  );
};
